/**
 * @fileoverview Property management actions, hooks, and utilities
 * @module PropertiesActions
 */

import axios from "axios";
import apiClient from "../utils/apiClient";
import download from "downloadjs";
import { atom } from "jotai";
import { format } from "date-fns";

import add from "date-fns/add";

import useSWR from "swr";
import normalizePropertyDescription from "../utils/normalizePropertyDescription";

function normalizePropertyPayload(data) {
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const description = data.get("description");
    if (typeof description !== "string") return data;

    const payload = new FormData();
    for (const [key, value] of data.entries()) payload.append(key, value);
    payload.set("description", normalizePropertyDescription(description));
    return payload;
  }

  if (typeof URLSearchParams !== "undefined" && data instanceof URLSearchParams) {
    const description = data.get("description");
    if (typeof description !== "string") return data;

    const payload = new URLSearchParams(data);
    payload.set("description", normalizePropertyDescription(description));
    return payload;
  }

  if (!data || typeof data.description !== "string") return data;

  return {
    ...data,
    description: normalizePropertyDescription(data.description),
  };
}

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */
/**
 * Hook that returns data from property feeds
 * @function
 * @name useOrigin
 * @memberof module:PropertiesActions
 * @returns {SWRObject} SWR Object containing feeds data
 */
export const useOrigin = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);
  const { data, error } = useSWR("/property/feeds?status=True", fetcher);
  return { data, isLoading: !error && !data, error };
};

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */
/**
 * Hook that returns the different types of properties
 * @function
 * @name usePropertyType
 * @memberof module:PropertiesActions
 * @returns {SWRObject} SWR Object containing property types data
 */
export const usePropertyType = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);
  const { data, error } = useSWR("/property_type", fetcher);
  return { data, isLoading: !error && !data, error };
};

/**
 * Parses and transforms price filter parameters based on operation type
 * @function
 * @name parsePriceFilter
 * @memberof module:PropertiesActions
 * @param {Object} params - Filter parameters object
 * @param {Array<number>} [params.operation_type] - Array of operation type IDs (1=sale, 2=rent, 3=transfer)
 * @param {string} [params.ordering] - Ordering field, typically "price" or "-price"
 * @returns {Object} Modified params object with operation-specific price ordering
 */
function parsePriceFilter(params) {
  if (params.operation_type?.includes(1) && params.ordering) {
    if (params.ordering.startsWith("-price")) params.ordering = "-price_sale";
    if (params.ordering.startsWith("price")) params.ordering = "price_sale";
  }
  if (params.operation_type?.includes(2) && params.ordering) {
    if (params.ordering.startsWith("-price")) params.ordering = "-price_rent";
    if (params.ordering.startsWith("price")) params.ordering = "price_rent";
  }
  if (params.operation_type?.includes(3) && params.ordering) {
    if (params.ordering.startsWith("-price")) params.ordering = "-price_transfer";
    if (params.ordering.startsWith("price")) params.ordering = "price_transfer";
  }
  if (
    (!params.operation_type ||
      params.operation_type?.length == 0 ||
      params.operation_type.length > 1) &&
    params.ordering
  ) {
    if (params.ordering.startsWith("-price")) params.ordering = "-price_sale";
    if (params.ordering.startsWith("price")) params.ordering = "price_sale";
  }
  return params;
}
/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */
/**
 * Hook that returns the properties published by the current client
 * @function
 * @name useMyProperties
 * @memberof module:PropertiesActions
 * @param {Object} params - Parameters for the properties (page, status, ordering)
 * @returns {SWRObject} SWR Object containing user's properties data
 */
export const useMyProperties = (params) => {
  const parsedParams = params ? parsePriceFilter(params) : {};
  const fetcher = ({ url, params }) =>
    apiClient
      .get(url, {
        params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          Object.entries(params).forEach(([key, value]) => {
            searchParams.append(key, Array.isArray(value) ? value.join(",") : value);
          });
          return searchParams.toString();
        },
      })
      .then((res) => res.data);
  const { data, error, mutate } = useSWR(
    params ? { url: "/my_property", params: parsedParams } : null,
    fetcher,
  );
  return { data, isLoading: !error && !data, error, mutate };
};

/**
 * Hook to fetch the count of user's properties by status
 * @function
 * @name useMyPropertiesCount
 * @memberof module:PropertiesActions
 * @returns {SWRObject} SWR object with property count data, loading state, error, and mutate function
 */
export const useMyPropertiesCount = () => {
  const fetcher = ({ url }) => apiClient.get(url).then((res) => res.data);
  const { data, error, mutate } = useSWR({ url: "/my_properties_count" }, fetcher);
  return { data, isLoading: !error && !data, error, mutate };
};

export const propertiesFilterInitialValues = {
  page: 1,
  status: "published",
  ordering: "-created",
};

export const propertiesFilterInventoryInitialValues = {
  page: 1,
  full: true,
  status: "published",
  ordering: "-created",
};

export const propertiesParamsAtom = atom(propertiesFilterInitialValues);
export const propertiesParamsInventoryAtom = atom(propertiesFilterInventoryInitialValues);

/**
 * Creates a new property
 * @function
 * @name createProperty
 * @memberof module:PropertiesActions
 * @param {Object} data - Values for the new property
 * @returns {Promise<Object|undefined>} API response if the property was created successfully
 */
export const createProperty = async (data) => {
  try {
    return await apiClient.post(`/property`, normalizePropertyPayload(data));
  } catch (err) {
    $log.error(err);
  }
};

/**
 * Generates and downloads a PDF for a property
 * @function
 * @name getPdf
 * @memberof module:PropertiesActions
 * @param {Object} property - Property data
 * @param {Object} contactData - Contact information for the user
 * @returns {Promise<void>}
 */
export const getPdf = async (property, contactData) => {
  try {
    const data = await axios({
      method: "POST",
      url: import.meta.env.VITE_PDF_API,
      headers: {
        Accept: "application/octet-stream",
        token: import.meta.env.VITE_PDF_TOKEN,
      },
      responseType: "blob",
      data: {
        property,
        contactData,
      },
    });
    download(data.data, `${property.id}.pdf`, "application/pdf");
  } catch (err) {
    $log.error(err);
  }
};

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */
/**
 * Hook that returns data for a specific property
 * @function
 * @name useProperty
 * @memberof module:PropertiesActions
 * @param {number} propertyId - ID for the property
 * @returns {SWRObject} SWR Object containing property data
 */
export const useProperty = (propertyId) => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);
  const { data, error } = useSWR(propertyId ? `property/${propertyId}` : null, fetcher);
  return { data, isLoading: !error && !data, error };
};

/**
 * Updates property data
 * @function
 * @name updateProperty
 * @memberof module:PropertiesActions
 * @param {number} propertyId - ID for the property
 * @param {Object} data - Values to update for the property
 * @returns {Promise<Object|undefined>} API response
 */
export const updateProperty = async (propertyId, data) => {
  try {
    return await apiClient.patch(`/property/${propertyId}`, normalizePropertyPayload(data));
  } catch (err) {
    $log.error(err);
  }
};

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */
/**
 * Hook that returns properties with search/filter parameters
 * @function
 * @name useProperties
 * @memberof module:PropertiesActions
 * @param {Object} params - Values for searching/filtering properties
 * @returns {SWRObject} SWR Object containing properties data
 */
export const useProperties = (params) => {
  const fetcher = ({ url, params }) =>
    apiClient
      .get(url, {
        params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          for (const key of Object.keys(params)) {
            const param = params[key];
            if (Array.isArray(param)) {
              searchParams.append(key, param.join(","));
            } else {
              searchParams.append(key, param);
            }
          }
          return searchParams.toString();
        },
      })
      .then((res) => res.data);
  const { data, error, mutate } = useSWR({ url: "/property", params }, fetcher);
  return { data, isLoading: !error && !data, error, mutate };
};

/**
 * Toggles a property's status (active/inactive) with optional confirmation dialog
 * @function
 * @name toggleProperty
 * @memberof module:PropertiesActions
 * @param {Object} actionObject - Action configuration object
 * @param {string} actionObject.status - New status for the property ('active', 'inactive', 'published')
 * @param {string|number} actionObject.id - Property ID to update
 * @param {boolean} [actionObject.confirm=false] - Whether to show confirmation dialog
 * @param {Function} [callBack] - Optional callback function to execute after successful update
 * @returns {Promise<void>}
 */
export const toggleProperty = async ({ status, id, confirm = false }, callBack) => {
  if (confirm && status === "inactive") {
    if (window.confirm("¿Estás seguro que quieres desactivar esta propiedad?")) {
      await apiClient.patch(`/property/${id}`, {
        status,
        id,
      });
      if (callBack) {
        callBack();
      }
    }
  } else if (confirm && status === "published") {
    if (window.confirm("¿Estás seguro que quieres activar esta propiedad?")) {
      await apiClient.patch(`/property/${id}`, {
        status,
        id,
      });
      if (callBack) {
        callBack();
      }
    }
  } else {
    await apiClient.patch(`/property/${id}`, {
      status,
      id,
    });
    if (callBack) {
      callBack();
    }
  }
};

/**
 * Toggles a property's featured status with optional confirmation dialog
 * @function
 * @name toggleFeatured
 * @memberof module:PropertiesActions
 * @param {Object} options - Toggle configuration object
 * @param {boolean} options.featured - Whether to feature or unfeature the property
 * @param {string|number} options.id - Property ID to update
 * @param {boolean} [options.confirmDialog=true] - Whether to show confirmation dialog
 * @returns {Promise<void>}
 */
export const toggleFeatured = async ({ featured, id, confirmDialog = true }) => {
  if (!confirmDialog) {
    await apiClient.patch(`/update_featured_property/`, {
      properties: [{ property_id: id, status: featured ? 1 : 2 }],
    });
  } else {
    if (featured) {
      if (window.confirm("¿Estás seguro que quieres destacar esta propiedad?")) {
        await apiClient.post(`/featured_property/`, {
          properties: [
            {
              property: id,
              start_date: format(new Date(), "yyyy-MM-dd"),
              end_date: format(
                add(new Date(), {
                  days: 90,
                }),
                "yyyy-MM-dd",
              ),
              status: featured ? 1 : 2,
            },
          ],
        });
      }
    } else if (!featured) {
      if (
        window.confirm("¿Estás seguro que quieres remover el estatus destacado de esta propiedad?")
      ) {
        await apiClient.patch(`/update_featured_property/`, {
          properties: [{ property_id: id, status: featured ? 1 : 2 }],
        });
      }
    } else {
      await apiClient.post(`/featured_property/`, {
        properties: [
          {
            property: id,
            start_date: format(new Date(), "yyyy-MM-dd"),
            end_date: format(
              add(new Date(), {
                days: 30,
              }),
              "yyyy-MM-dd",
            ),
            status: featured ? 1 : 2,
          },
        ],
      });
    }
  }
};

/**
 * Hook to get CTR, visits and leads statistics for a property
 * @function
 * @name usePropertyStats
 * @memberof module:PropertiesActions
 * @param {number} propertyId - The property ID to get stats for
 * @returns {SWRObject} SWR Object containing property statistics
 */

export const usePropertyStats = (propertyId) => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);
  const { data, error } = useSWR(propertyId ? `stats_my_property/${propertyId}` : null, fetcher);
  return { data, isLoading: !error && !data, error };
};
